import React, { useMemo } from 'react';
import { ICONS } from 'Components/utils/icon/icons';
import { t } from 'Src/i18n';
import { CapsuleCollapseLists } from 'Components/frameworks/capsule-collapse-lists/CapsuleCollapseLists';
type Equipment = {
  id: string;
  label: string;
};
type GroupedFacility = {
  title: string;
  equipments: Map<string, Equipment>;
};
type Props = {
  className?: string;
  facilities: Product['facilities'];
};
export const ChildcareIncludedEquipmentCapsule = ({ className, facilities }: Props) => {
  const deduplicatedLists = useMemo(() => {
    if (!facilities || facilities.length === 0) {
      return [];
    }

    // Group facilities by subtitle (merge facilities with the same subtitle)
    const groupedBySubtitle: Record<string, GroupedFacility> = facilities.reduce(
      (acc, facility) => {
        const subtitle = facility.subtitle || '';
        const title = facility.subtitle || facility.label || '';

        if (!acc[subtitle]) {
          acc[subtitle] = {
            title,
            equipments: new Map(),
          };
        }

        // Merge equipments from facilities with same subtitle
        (facility.equipments || []).forEach((equipment) => {
          if (equipment.id && !acc[subtitle].equipments.has(equipment.id)) {
            acc[subtitle].equipments.set(equipment.id, equipment);
          }
        });
        return acc;
      },
      {},
    );
    // track which equipment appears in which facilities (by subtitle)
    const equipmentToFacilities = new Map<string, Array<string>>();
    Object.keys(groupedBySubtitle).forEach((subtitle) => {
      const group = groupedBySubtitle[subtitle];
      group.equipments.forEach((equipment) => {
        if (!equipmentToFacilities.has(equipment.id)) {
          equipmentToFacilities.set(equipment.id, []);
        }

        const facilityList = equipmentToFacilities.get(equipment.id);

        if (facilityList) {
          facilityList.push(subtitle);
        }
      });
    });
    // For each facility group, remove equipments that appear in other facilities
    // Keep equipment only in the first facility where it appears
    const facilityOrder = Object.keys(groupedBySubtitle);
    const processedLists = facilityOrder.map((subtitle, index) => {
      const group = groupedBySubtitle[subtitle];
      const filteredEquipments = Array.from(group.equipments.values()).filter((equipment) => {
        const facilitiesWithEquipment = equipmentToFacilities.get(equipment.id) || [];

        // If equipment appears in only one facility, keep it
        if (facilitiesWithEquipment.length === 1) {
          return true;
        }

        // If equipment appears in multiple facilities, keep it only in the first one
        const firstFacilityIndex = facilityOrder.findIndex((key) =>
          facilitiesWithEquipment.includes(key),
        );
        return firstFacilityIndex === index;
      });
      return {
        title: group.title,
        items: filteredEquipments.map((equipment) => ({
          iconName: ICONS[equipment.id],
          label: equipment.label,
        })),
      };
    });
    return processedLists;
  }, [facilities]);
  return (
    <CapsuleCollapseLists
      className={className}
      header={{
        iconName: ICONS.ALL_INCLUSIVE,
        title: t('common.allInclusive'),
      }}
      lists={deduplicatedLists}
    />
  );
};
